<script setup lang="ts">
import { useI18n } from "vue-i18n";

import type { SelectOption } from "@/dsComponents/inputs/types";
import type { LabelValue } from '@/types/common';

import ButtonAlt from "@/dsComponents/buttons/ButtonAlt.vue";
import Autocomplete from "@/dsComponents/inputs/Autocomplete.vue";
import Select from "@/dsComponents/inputs/Select.vue";
import SideModal from "@/dsComponents/modals/SideModal.vue";

interface WorkerJobEditModalProps {
    open: boolean;
    isNewJob: boolean;
    loading: boolean;
    saveDisabled: boolean;
    jobOptions: LabelValue<string>[];
    degreeOptions: LabelValue<string>[];
    experienceOptions: LabelValue[];
}

defineProps<WorkerJobEditModalProps>();

const jobName = defineModel<string>("jobName", { required: true });
const degree = defineModel<string>("degree", { required: true });
const experience = defineModel<string>("experience", { required: true });

const emit = defineEmits<{
    close: [];
    save: [];
    "select-job": [option: SelectOption];
    "select-degree": [option: SelectOption];
    "select-experience": [option: SelectOption];
}>();

const { t } = useI18n();
</script>
<template>
    <SideModal :open="open" :title="isNewJob ? t('page.interim.addJob') : t('page.interim.editJob')" :width="420" @close="emit('close')">
        <div class="worker-job-edit-modal__form">
            <Autocomplete
                v-model="jobName"
                :placeholder="t('page.jobOffer.add.form.chooseJob')"
                :label="t('form.job')"
                :options="jobOptions"
                width="100%"
                @select="(value: string | SelectOption) => { if (typeof value !== 'string') emit('select-job', value) }"
            />
            <Select
                v-model="degree"
                :label="t('page.interim.administrativeDocuments.degree')"
                :options="degreeOptions"
                :placeholder="t('page.jobOffer.add.form.chooseDegree')"
                width="100%"
                @select="(value) => emit('select-degree', value)"
            />
            <Select
                v-model="experience"
                :label="t('form.experience')"
                :options="experienceOptions"
                :placeholder="t('page.jobOffer.add.form.chooseExperience')"
                width="100%"
                @select="(value) => emit('select-experience', value)"
            />
        </div>
        <template #footer-actions>
            <ButtonAlt type="neutral" size="sm" :label="t('global.actions.cancel')" :loading="loading" @click="emit('close')" />
            <ButtonAlt size="sm" variant="filled" :label="t('global.actions.add')" :disabled="saveDisabled" :loading="loading" @click="emit('save')" />
        </template>
    </SideModal>
</template>
<style scoped lang="scss">
.worker-job-edit-modal__form {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-3xl, 24px);
}
</style>
